import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

import { labelFor } from '../../../lib/audit-labels';
import { supabase } from '../../../lib/supabase';
import { projectStatusLabel } from './projects.schema';
import styles from './projects.module.css';

interface FeedRow {
  id: string;
  text: string;
  created_at: string;
}

/** "סטטוס שונה ל-active" → "סטטוס שונה ל-פעיל". The RPC writes the raw value. */
function localizeSummary(summary: string): string {
  return summary.replace(/ל-([a-z_]+)/g, (_m, status: string) => `ל-${projectStatusLabel(status)}`);
}

async function fetchFeed(orgId: string, projectId: string): Promise<FeedRow[]> {
  const [audit, activities] = await Promise.all([
    supabase
      .from('audit_log')
      .select('id, action, created_at')
      .eq('organization_id', orgId)
      .eq('entity_id', projectId)
      .order('created_at', { ascending: false })
      .limit(50),
    supabase
      .from('activities')
      .select('id, summary, created_at')
      .eq('organization_id', orgId)
      .eq('entity_type', 'project')
      .eq('entity_id', projectId)
      .order('created_at', { ascending: false })
      .limit(50),
  ]);
  if (audit.error) throw audit.error;
  if (activities.error) throw activities.error;

  return [
    ...audit.data.map((r) => ({
      id: `a-${r.id}`,
      text: labelFor(r.action),
      created_at: r.created_at,
    })),
    ...activities.data.map((r) => ({
      id: `t-${r.id}`,
      text: localizeSummary(r.summary),
      created_at: r.created_at,
    })),
  ].sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export function ProjectActivityTab({ orgId, projectId }: { orgId: string; projectId: string }) {
  const {
    data: rows = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['projects', orgId, 'activity', projectId] as const,
    queryFn: () => fetchFeed(orgId, projectId),
    staleTime: 15 * 1000,
  });

  if (isLoading) return <p className={styles.notice}>טוען פעילות...</p>;
  if (error) return <p className={styles.notice}>לא הצלחנו לטעון פעילות</p>;
  if (rows.length === 0) return <p className={styles.notice}>אין פעילות מתועדת.</p>;

  return (
    <div className={styles.activityList}>
      {rows.map((row) => (
        <div key={row.id} className={styles.activityRow}>
          <span className={styles.activityAction}>{row.text}</span>
          <span className={styles.activityTime}>
            {format(new Date(row.created_at), 'dd/MM/yy HH:mm', { locale: he })}
          </span>
        </div>
      ))}
    </div>
  );
}
